// ManualDriftCard — dashboard card showing manual-priced SKUs that have drifted
// away from the current competitor recommendation by >= threshold_pct.
// Consumes GET /api/pricing/manual-drift (Task 3).
// Per-row mute via POST /api/price/suppress {sku, market_code, hours:336}.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshButton from './common/RefreshButton';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { alpha } from '@mui/material/styles';
import { toast } from 'sonner';
import { FS, SHADOWS, hoverOnly } from '../theme/tokens';
import Pill from './common/Pill';
import { formatPercent } from '../utils/format';
import { marketLabel } from '../utils/market';
// UI2 Teil 9, Karte Z-1c: nach der Erholung laedt die Karte selbst neu.
import useReloadOnRecovery from '../serviceOutage/useReloadOnRecovery';

const API = process.env.REACT_APP_API_BASE_URL || '';

const fmt = (value) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value ?? 0);

/** Relative "last updated" label from an ISO timestamp (snapshot freshness). */
const fmtAgo = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'Updated <1 min ago';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `Updated ${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Updated ${hrs}h ago`;
  return `Updated ${Math.floor(hrs / 24)}d ago`;
};

/** Color drift% by magnitude using theme palette tokens — no hardcoded hex. */
function driftColor(pct) {
  const abs = Math.abs(pct ?? 0);
  if (abs >= 20) return 'error.main';
  if (abs >= 10) return 'warning.main';
  return 'info.main';
}

function driftBg(pct) {
  return (t) => {
    const abs = Math.abs(pct ?? 0);
    if (abs >= 20) return alpha(t.palette.error.main, 0.12);
    if (abs >= 10) return alpha(t.palette.warning.main, 0.12);
    return alpha(t.palette.info.main, 0.12);
  };
}

// 🔴 `drift_pct` ist BEREITS ein Prozentwert (`>= 20` im Vergleich drei Zeilen
// höher) — `formatPercent` nimmt einen Bruch, also hier durch 100. Ohne das
// verschöbe sich die Zahl um zwei Stellen (Grill W9). Das Vorzeichen `+` bleibt
// als Präfix erhalten.
const driftPctText = (pct) => (pct == null ? '—' : formatPercent(pct / 100, { digits: 1 }));
const driftChipText = (pct) =>
  (pct == null ? '—' : `${pct > 0 ? '+' : ''}${formatPercent(pct / 100, { digits: 1 })}`);


// ── Loading skeleton ────────────────────────────────────────────────────────

function DriftSkeleton() {
  return (
    <Stack spacing={1} sx={{ p: 2 }}>
      {[1, 2, 3].map((i) => (
        <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="40%" />
          </Box>
          <Skeleton variant="text" width={60} />
          <Skeleton variant="text" width={60} />
          <Skeleton variant="rectangular" width={80} height={28} sx={{ borderRadius: 1 }} />
        </Box>
      ))}
    </Stack>
  );
}

// ── Per-row drift item ──────────────────────────────────────────────────────

function DriftRow({ row, onMute, muting }) {
  const navigate = useNavigate();
  const validUrl =
    typeof row.competitor_url === 'string' && /^https?:\/\//.test(row.competitor_url);

  return (
    <Box
      onClick={() => navigate(`/products/${row.sku}`)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
        cursor: 'pointer',
        transition: 'background 0.15s',
        ...hoverOnly({ bgcolor: 'action.hover' }),
        minWidth: 0,
      }}
    >
      {/* Product info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {row.product_title || row.sku}
          {row.variant_name ? ` · ${row.variant_name}` : ''}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.25 }}>
          <Chip
            label={marketLabel(row.market_code)}
            size="small"
            sx={{ height: 18, fontSize: FS.xxs }}
          />
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: FS.xxs }}>
            {row.sku}
          </Typography>
          {validUrl && (
            <Link
              href={row.competitor_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: FS.xxs, color: 'text.secondary' }}
            >
              {row.competitor_shop}
              <OpenInNewIcon sx={{ fontSize: 10 }} />
            </Link>
          )}
          {!validUrl && row.competitor_shop && (
            <Typography variant="caption" sx={{ fontSize: FS.xxs, color: 'text.secondary' }}>
              {row.competitor_shop}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Price comparison */}
      <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: 80 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {fmt(row.our_total)}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
          → {fmt(row.recommended_total)}
        </Typography>
      </Box>

      {/* Drift badge */}
      <span
        role="img"
        aria-label={`drift ${driftPctText(row.drift_pct)}, ${Math.abs(row.drift_pct ?? 0) >= 20 ? 'high' : Math.abs(row.drift_pct ?? 0) >= 10 ? 'medium' : 'low'}`}
      >
        <Chip
          label={driftChipText(row.drift_pct)}
          size="small"
          sx={{
            height: 20,
            fontSize: FS.xxs,
            fontWeight: 700,
            flexShrink: 0,
            bgcolor: driftBg(row.drift_pct),
            color: driftColor(row.drift_pct),
            border: 'none',
          }}
        />
      </span>

      {/* Mute button */}
      <Tooltip title="Mute for 2 weeks — suppresses drift alerts for this SKU/market">
        <span>
          <IconButton
            size="small"
            disabled={muting}
            onClick={(e) => {
              e.stopPropagation();
              onMute(row.sku, row.market_code);
            }}
            aria-label={`Mute ${row.sku} / ${row.market_code} for 2 weeks`}
            sx={{ color: 'text.disabled', ...hoverOnly({ color: 'warning.main' }) }}
          >
            {muting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <VolumeOffIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export default function ManualDriftCard() {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mutingIds, setMutingIds] = useState(new Set());

  const fetchData = () => {
    setLoading(true);
    setError(null);
    fetch(`${API}/api/pricing/manual-drift`)
      .then((r) => {
        // UI2 Teil 9, Karte Z-1c (E-3/Z-1b-Form): kein roher Statuscode. Der
        // Status geht ins Protokoll — aber an der EINEN Meldung unten, nicht
        // hier: sonst stuenden zwei Zeilen mit demselben Praefix im Log
        // (R15-W4/R16-W-3, Nachbesserung 1).
        if (!r.ok) {
          const fehler = new Error('Could not load the drift data.');
          fehler.status = r.status;
          throw fehler;
        }
        return r.json();
      })
      .then((d) => {
        setData(d);
        setRows(d.rows || []);
      })
      .catch((err) => {
        // Genau EINE Meldung je Fehlschlag. Sie traegt den Status, wenn es
        // einen gibt; ein Netzfehler hat keinen und geht als Fehlerobjekt mit.
        console.error(
          'ManualDriftCard: fetch failed',
          err?.status != null ? { status: err.status } : err
        );
        setError(err?.message || 'Could not load the drift data.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fetch once on mount; refresh via button

  // 🔴 UI2 Teil 9, Karte Z-1c (E-3): der BESTEHENDE Ladeweg, kein zweiter.
  // `fetchData` ist hier je Render neu — der Hook haelt die juengste Referenz.
  useReloadOnRecovery(fetchData);

  const handleMute = async (sku, market_code) => {
    const key = `${sku}::${market_code}`;
    setMutingIds((prev) => new Set([...prev, key]));
    try {
      const res = await fetch(`${API}/api/price/suppress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sku, market_code, hours: 336 }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        console.error('ManualDriftCard: mute failed', errData);
        toast.error('Could not mute - try again');
        return;
      }
      // Remove the row only after confirmed success
      setRows((prev) => prev.filter((r) => !(r.sku === sku && r.market_code === market_code)));
    } catch (err) {
      console.error('ManualDriftCard: mute error', err);
      toast.error('Could not mute - try again');
    } finally {
      setMutingIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const threshold = data?.threshold_pct ?? 0;
  const totalManual = data?.counts?.total_manual ?? 0;
  const drifting = rows.length;

  return (
    <Paper
      data-testid="manual-drift-card"
      sx={{
        overflow: 'hidden',
        borderRadius: 3,
        boxShadow: SHADOWS.panel,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* 🔴 W-33/D8: der Farbverlauf `linear-gradient(135deg, #f093fb, #f5576c)`
          ist weg, mit ihm `color: 'white'` an Text und Symbol. Der Verlauf trug
          keine Information, die die Zaehl-Pille nicht traegt — er machte die
          Karte wichtig, und genau das leistet der Abschnittskopf plus die Zahl.
          Die Drift-Zahl steht jetzt als `Pill` (Warn-Ton) statt als Satz in der
          Kopfzeile: eine Zahl, die man mit einer anderen Karte vergleichen
          will, gehoert in dieselbe Form wie ueberall sonst. */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <TrendingUpIcon sx={{ color: 'warning.main' }} />
          <Box sx={{ minWidth: 0 }}>
            {/* 🔴 F8 (2026-10-08) → Nachbesserung 2 (R16 B1): der Name steht
                wieder als Abschnitts-`h6` UEBER der Karte (`HomeDashboard`),
                hier faellt er. F8 hatte T3-9 („derselbe Text zweimal
                untereinander") von der anderen Seite geloest und dabei die
                Abschnittshierarchie der Seite auf zwei Groessen gestellt: vier
                Abschnitte in 1,25 rem (`h6`), dieser in 0,875 rem
                (`subtitle2`), bei gleicher Dokumentstufe.
                🔴 Der hier entfernte Titel trug `component="h6"`, und das war
                REDUNDANT — MUI bildet `subtitle2` per `defaultVariantMapping`
                ohnehin auf `h6` ab (`@mui/material/Typography/Typography.js`,
                `defaultVariantMapping`), das Theme setzt kein `variantMapping`
                (`grep -rn variantMapping frontend/src/theme/` → leer). Wer den
                Titel zurueckholt, bekommt also ohne jedes Attribut eine
                UEBERSCHRIFT — und damit ZWEI mit demselben Namen, weshalb
                `home/HomeDashboard.test.jsx` dann rot wird. */}
            {/* 🔴 Nachbesserung 3 (2026-10-08): die Zaehl-Pille hing nach dem
                Titelfall ALLEIN in einer eigenen Zeile UEBER der Beschreibung
                (Fotobefund 1440 px, „Manual price drift"). Eine nackte Zahl
                ueber dem Satz, den sie zaehlt, liest sich wie ein Fehler. Sie
                steht jetzt rechts HINTER der Beschreibung in derselben Zeile
                und wandert bei Umbruch mit (`flexWrap`) — statt eine eigene
                Zeile zu belegen.
                🔴 Damit ist auch die Huelle keine verwaiste mehr: sie trug nach
                dem Titelfall nur noch EIN Kind (die Pille) und damit keine
                Zeile, sondern nur einen Abstand. Hausform ist der Zeilenkopf
                von „Scraper targets" (`HomeDashboard.jsx`: `Stack` mit `h6` +
                Pille) und die Statuszeile derselben Karte (`Box` mit
                `flexWrap`). */}
            <Stack direction="row" alignItems="center" gap={1} sx={{ flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                Manual prices deviating from current recommendation
              </Typography>
              {/* 🔴 Kein `aria-label`, und die Nachbarpille ist der Beleg: die
                  Zaehl-Pille an „Scraper targets" (`HomeDashboard.jsx`) traegt
                  ebenfalls keins. Der Name kommt dort wie hier aus der
                  NACHBARSCHAFT — die Zahl steht in der Leserichtung HINTER dem
                  Text, den sie zaehlt. Ein eigenes Label wuerde denselben Satz
                  ein zweites Mal vorlesen („… recommendation 0 manual prices
                  drifting"). */}
              {!loading && !error && (
                <Pill tone="warn" count={drifting} data-testid="manual-drift-count" />
              )}
            </Stack>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
              {loading
                ? 'Loading…'
                : `${totalManual} on manual, threshold ${threshold}%`}
              {data?.generated_at ? ` · ${fmtAgo(data.generated_at)}` : ''}
            </Typography>
          </Box>
        </Box>
        {/* E8 (2026-10-07): handgezogener Symbol-Knopf mit Spinner und
            `disabled` — genau die Bauform, die `RefreshButton` jetzt einmal
            traegt.
            🔴 `firstLoad`: im Aufbau steht unten `DriftSkeleton`, der Knopf
            dreht sich dann nicht mit (Anti-Pattern 11). `data === null` ist
            der Erstaufbau — ein Fehler setzt ebenfalls `null`, dann ist
            `loading` aber schon `false`. */}
        <RefreshButton
          ariaLabel="Reload manual drift snapshot"
          tooltip="Reload manual drift snapshot"
          loading={loading}
          firstLoad={loading && !data}
          onClick={fetchData}
        />
      </Box>

      {/* Body */}
      {loading ? (
        <DriftSkeleton />
      ) : error ? (
        <Box sx={{ p: 2 }}>
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={fetchData}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No manual prices are drifting
          </Typography>
          {totalManual > 0 && (
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
              {totalManual} SKUs on manual — all within {threshold}% of recommendation
            </Typography>
          )}
        </Box>
      ) : (
        <>
          <Divider />
          <Box>
            {rows.map((row) => (
              <DriftRow
                key={`${row.sku}::${row.market_code}`}
                row={row}
                onMute={handleMute}
                muting={mutingIds.has(`${row.sku}::${row.market_code}`)}
              />
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
}
